import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ShieldCheck, ChatCircleDots, Lock, Eye } from '@phosphor-icons/react';

interface AIDataConsentModalProps {
  isOpen: boolean;
  onConsent: () => void;
  onDecline: () => void;
}

export default function AIDataConsentModal({ isOpen, onConsent, onDecline }: AIDataConsentModalProps) {
  const [loading, setLoading] = useState(false);

  const handleConsent = async () => {
    setLoading(true);
    try {
      onConsent();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onDecline}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <ShieldCheck size={24} className="text-pink-500" weight="fill" />
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">How Romy Uses Your Data</h2>
          </div>
          <button
            onClick={onDecline}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            When you chat with Romy, your messages and health information are processed by an{' '}
            <strong>AI language model</strong> to generate personalized responses.
          </p>

          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <ChatCircleDots size={20} className="text-pink-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">What data is sent</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Your chat messages, health profile, and relevant health logs are sent to generate responses.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Eye size={20} className="text-pink-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Who receives it</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Data is processed by our <strong>AI service provider</strong> solely to generate your responses. No other third parties receive your data.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Lock size={20} className="text-pink-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">How it's protected</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Your data is <strong>not used to train AI models</strong>. It is not shared with data brokers, advertisers, or any other parties.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Read our full{' '}
            <Link to="/privacy" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
              Privacy Policy
            </Link>{' '}
            for details on data collection, use, and protection.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onDecline}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleConsent}
            disabled={loading}
            className="px-5 py-2 text-sm text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'I Understand & Agree'}
          </button>
        </div>
      </div>
    </div>
  );
}
